import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "poll-editor__caption" }
const _hoisted_2 = { class: "poll-editor__wrapper" }
const _hoisted_3 = { class: "poll-editor__caption" }
const _hoisted_4 = { class: "poll-editor__caption" }
const _hoisted_5 = { class: "poll-editor__settings" }

import type { createPollParams, requiredPollParams } from '../../types/index.ts'

import { showError } from '@nextcloud/dialogs'
import { t } from '@nextcloud/l10n'
import { computed, nextTick, reactive, ref, useTemplateRef } from 'vue'
import { useStore } from 'vuex'
import NcActionButton from '@nextcloud/vue/components/NcActionButton'
import NcActionLink from '@nextcloud/vue/components/NcActionLink'
import NcActions from '@nextcloud/vue/components/NcActions'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcCheckboxRadioSwitch from '@nextcloud/vue/components/NcCheckboxRadioSwitch'
import NcDialog from '@nextcloud/vue/components/NcDialog'
import NcIconSvgWrapper from '@nextcloud/vue/components/NcIconSvgWrapper'
import NcTextField from '@nextcloud/vue/components/NcTextField'
import IconArrowLeft from 'vue-material-design-icons/ArrowLeft.vue'
import Close from 'vue-material-design-icons/Close.vue'
import IconFileEditOutline from 'vue-material-design-icons/FileEditOutline.vue'
import Plus from 'vue-material-design-icons/Plus.vue'
import IconFileDownload from '../../../img/material-icons/file-download.svg?raw'
import IconFileUpload from '../../../img/material-icons/file-upload.svg?raw'
import { useGetThreadId } from '../../composables/useGetThreadId.ts'
import { useGetToken } from '../../composables/useGetToken.ts'
import { POLL } from '../../constants.ts'
import { hasTalkFeature } from '../../services/CapabilitiesManager.ts'
import { EventBus } from '../../services/EventBus.ts'
import { usePollsStore } from '../../stores/polls.ts'
import { convertToJSONDataURI } from '../../utils/fileDownload.ts'
import { validatePollForm } from '../../utils/validatePollForm.ts'


export default /*@__PURE__*/_defineComponent({
  __name: 'PollEditor',
  props: {
    token: {},
    canCreatePollDrafts: { type: Boolean },
    container: {}
  },
  emits: ["close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props
const emit = __emit
__expose({
	fillPollEditorFromDraft,
})

const supportPollDrafts = hasTalkFeature(props.token, 'talk-polls-drafts')

const store = useStore()
const pollsStore = usePollsStore()
const currentConversationToken = useGetToken()
const threadId = useGetThreadId()

const isOpenedFromDraft = ref(false)
const editingDraftId = ref<number | null>(null)
const pollOption = useTemplateRef<InstanceType<typeof NcTextField>[]>('pollOption')
const pollImport = ref<HTMLInputElement | null>(null)

const pollForm = reactive<createPollParams>({
	question: '',
	options: ['', ''],
	resultMode: POLL.MODE.PUBLIC,
	maxVotes: POLL.ANSWER_TYPE.SINGLE,
})

const isFilled = computed(() => Boolean(pollForm.question) && pollForm.options.filter((option) => Boolean(option)).length >= 2)
const dialogName = computed(() => {
	return editingDraftId.value ? t('spreed', 'Edit poll draft') : t('spreed', 'Create new poll')
})
const createPollLabel = computed(() => {
	if (editingDraftId.value) {
		return t('spreed', 'Save')
	}

	return currentConversationToken.value !== props.token
		? t('spreed', 'Create poll in {name}', { name: store.getters.conversation(props.token)?.displayName ?? '' }, undefined, { escape: false, sanitize: false })
		: t('spreed', 'Create poll')
})

const isAnonymous = computed({
	get() {
		return pollForm.resultMode === POLL.MODE.HIDDEN
	},
	set(value) {
		pollForm.resultMode = value ? POLL.MODE.HIDDEN : POLL.MODE.PUBLIC
	},
})

const isMultipleAnswer = computed({
	get() {
		return pollForm.maxVotes === POLL.ANSWER_TYPE.MULTIPLE
	},
	set(value) {
		pollForm.maxVotes = value ? POLL.ANSWER_TYPE.MULTIPLE : POLL.ANSWER_TYPE.SINGLE
	},
})

const exportPollURI = computed(() => convertToJSONDataURI(pollForm))
const exportPollFileName = `Talk Poll ${new Date().toISOString().slice(0, 10)}`

/**
 * Remove a previously added option
 *
 * @param index option index
 */
function deleteOption(index: number) {
	pollForm.options.splice(index, 1)
}

/**
 * Add an empty option to the form
 */
function addOption() {
	pollForm.options.push('')
	nextTick(() => {
		pollOption.value!.at(-1)!.focus()
	})
}

/**
 * Post a poll into conversation
 */
async function handleSubmit() {
	if (editingDraftId.value) {
		const pollDraft = await pollsStore.updatePollDraft({
			token: props.token,
			pollId: editingDraftId.value,
			form: pollForm,
		})
		if (pollDraft) {
			openPollDraftHandler()
			nextTick(() => {
				emit('close')
			})
		}
		return
	}

	const poll = await pollsStore.createPoll({
		token: props.token,
		threadId: threadId.value ? threadId.value : undefined,
		form: pollForm,
	})
	if (poll) {
		emit('close')
	}
}

/**
 * Pre-fills form from the draft
 *
 * @param id poll draft ID
 * @param fromDrafts whether editor was opened from drafts handler
 * @param action required action ('fill' from draft or 'edit' draft)
 */
function fillPollEditorFromDraft(id: number | null, fromDrafts: boolean, action?: string) {
	if (fromDrafts) {
		// Show 'Back' button, do not reset until closed
		isOpenedFromDraft.value = true
	}
	if (action === 'edit') {
		// Show Edit interface
		editingDraftId.value = id
	}

	if (id === null) {
		return
	}

	const draft = pollsStore.drafts[props.token][id]
	if (draft) {
		fillPollForm({
			question: draft.question,
			options: [...draft.options],
			resultMode: draft.resultMode,
			maxVotes: draft.maxVotes,
		})
	}
}

/**
 * Call native input[type='file'] to import a file
 */
function triggerImport() {
	pollImport.value!.click()
}

/**
 * Validate imported file and insert data into form fields
 *
 * @param event import event
 */
function importPoll(event: Event) {
	const file = (event.target as HTMLInputElement).files?.[0]
	if (!file) {
		return
	}

	const reader = new FileReader()
	reader.onload = (e: ProgressEvent) => {
		try {
			const parsedObject = validatePollForm(JSON.parse((e.target as FileReader).result as string))
			fillPollForm(parsedObject)
		} catch (error) {
			showError(t('spreed', 'Error while importing poll'))
			console.error('Error while importing poll:', error)
		}
	}

	reader.readAsText(file)
}

/**
 * Insert data into form fields
 *
 * @param payload data to fill with
 */
function fillPollForm(payload: requiredPollParams) {
	Object.assign(pollForm, payload)
}

/**
 * Saves a poll draft for this conversation
 */
async function createPollDraft() {
	await pollsStore.createPollDraft({
		token: props.token,
		form: pollForm,
	})
}

/**
 * Open a PollDraftHandler dialog
 */
function openPollDraftHandler() {
	EventBus.emit('poll-drafts-open', { token: props.token, selector: props.container })
}

/**
 * Open a PollDraftHandler dialog as Back action
 */
function goBack() {
	openPollDraftHandler()
	if (isOpenedFromDraft.value) {
		nextTick(() => {
			emit('close')
		})
	}
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(NcDialog), {
    name: dialogName.value,
    "close-on-click-outside": !isFilled.value,
    container: __props.container,
    "onUpdate:open": _cache[3] || (_cache[3] = ($event: any) => (emit('close')))
  }, {
    actions: _withCtx(() => [
      (_unref(supportPollDrafts))
        ? (_openBlock(), _createBlock(_unref(NcActions), {
            key: 0,
            "force-menu": ""
          }, {
            default: _withCtx(() => [
              (props.canCreatePollDrafts && !editingDraftId.value)
                ? (_openBlock(), _createBlock(_unref(NcActionButton), {
                    key: 0,
                    disabled: !isFilled.value,
                    onClick: createPollDraft
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(IconFileEditOutline, { size: 20 })
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Save as draft')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true),
              (isFilled.value)
                ? (_openBlock(), _createBlock(_unref(NcActionLink), {
                    key: 1,
                    href: exportPollURI.value,
                    download: exportPollFileName
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(_unref(NcIconSvgWrapper), {
                        svg: _unref(IconFileDownload),
                        size: 20
                      }, null, 8, ["svg"])
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Export draft to file')), 1)
                    ]),
                    _: 1
                  }, 8, ["href"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_unref(NcButton), {
        variant: "primary",
        disabled: !isFilled.value,
        onClick: handleSubmit
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(createPollLabel.value), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ]),
    default: _withCtx(() => [
      (_unref(supportPollDrafts) && isOpenedFromDraft.value)
        ? (_openBlock(), _createBlock(_unref(NcButton), {
            key: 0,
            class: "poll-editor__back-button",
            variant: "tertiary",
            title: _unref(t)('spreed', 'Back'),
            "aria-label": _unref(t)('spreed', 'Back'),
            onClick: goBack
          }, {
            icon: _withCtx(() => [
              _createVNode(IconArrowLeft, {
                class: "bidirectional-icon",
                size: 20
              })
            ]),
            _: 1
          }, 8, ["title", "aria-label"]))
        : _createCommentVNode("", true),
      _createElementVNode("p", _hoisted_1, _toDisplayString(_unref(t)('spreed', 'Question')), 1),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(NcTextField), {
          modelValue: pollForm.question,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pollForm.question) = $event)),
          label: _unref(t)('spreed', 'Ask a question')
        }, null, 8, ["modelValue", "label"]),
        _createElementVNode("input", {
          id: "poll-upload",
          ref_key: "pollImport",
          ref: pollImport,
          type: "file",
          class: "hidden-visually",
          onChange: importPoll
        }, null, 544),
        (_unref(supportPollDrafts))
          ? (_openBlock(), _createBlock(_unref(NcActions), {
              key: 0,
              "force-menu": ""
            }, {
              default: _withCtx(() => [
                (props.canCreatePollDrafts && !isOpenedFromDraft.value)
                  ? (_openBlock(), _createBlock(_unref(NcActionButton), {
                      key: 0,
                      "close-after-click": "",
                      onClick: openPollDraftHandler
                    }, {
                      icon: _withCtx(() => [
                        _createVNode(IconFileEditOutline, { size: 20 })
                      ]),
                      default: _withCtx(() => [
                        _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Browse poll drafts')), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_unref(NcActionButton), {
                  "close-after-click": "",
                  onClick: triggerImport
                }, {
                  icon: _withCtx(() => [
                    _createVNode(_unref(NcIconSvgWrapper), {
                      svg: _unref(IconFileUpload),
                      size: 20
                    }, null, 8, ["svg"])
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Import draft from file')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)('spreed', 'Answers')), 1),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pollForm.options, (option, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: "poll-editor__option"
        }, [
          _createVNode(_unref(NcTextField), {
            ref_for: true,
            ref_key: "pollOption",
            ref: pollOption,
            modelValue: pollForm.options[index],
            "onUpdate:modelValue": ($event: any) => ((pollForm.options[index]) = $event),
            label: _unref(t)('spreed', 'Answer {option}', { option: index + 1 })
          }, null, 8, ["modelValue", "onUpdate:modelValue", "label"]),
          (pollForm.options.length > 2)
            ? (_openBlock(), _createBlock(_unref(NcButton), {
                key: 0,
                variant: "tertiary",
                "aria-label": _unref(t)('spreed', 'Delete poll option'),
                onClick: ($event: any) => (deleteOption(index))
              }, {
                icon: _withCtx(() => [
                  _createVNode(Close, { size: 20 })
                ]),
                _: 1
              }, 8, ["aria-label", "onClick"]))
            : _createCommentVNode("", true)
        ]))
      }), 128)),
      _createVNode(_unref(NcButton), {
        class: "poll-editor__add-more",
        variant: "tertiary",
        onClick: addOption
      }, {
        icon: _withCtx(() => [
          _createVNode(Plus)
        ]),
        default: _withCtx(() => [
          _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Add answer')), 1)
        ]),
        _: 1
      }),
      _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)('spreed', 'Settings')), 1),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_unref(NcCheckboxRadioSwitch), {
          modelValue: isAnonymous.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((isAnonymous).value = $event)),
          type: "checkbox"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('spreed', 'Anonymous poll')), 1)
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_unref(NcCheckboxRadioSwitch), {
          modelValue: isMultipleAnswer.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isMultipleAnswer).value = $event)),
          type: "checkbox"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('spreed', 'Multiple answers')), 1)
          ]),
          _: 1
        }, 8, ["modelValue"])
      ])
    ]),
    _: 1
  }, 8, ["name", "close-on-click-outside", "container"]))
}
}

})